// =============================================================================
// ExperiencesTab — location-side primary editor for which experiences run here
// =============================================================================
// Header location switcher selects the current location. The body lists the
// experiences linked to that location, each with its own availability window
// and (for multi-room locations) a room subset selector. Empty room set =
// "runs in every bookable room at this location" — UI treats no-room-picker
// the same way for single-room locations.
//
// "Bewerken metadata" on each row opens the inline metadata editor (price /
// duration / image / slug) that used to live as the standalone Experiences
// tab. That editor also has an "Ook beschikbaar op" multi-location pane
// powered by fn_admin_set_experience_locations — the experience-side mirror
// of the same booking.location_experience junction.
// =============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../lib/auth';
import { formatError } from '../../lib/errors';
import {
  fetchWalkinRefData,
  type BookingLocation,
} from '../../lib/walkin-data';
import {
  getLocationExperiences,
  saveLocationExperiences,
  listExperiencesFull,
  editExperience,
  getExperienceLocations,
  setExperienceLocations,
  getExperienceLanguages,
  saveExperienceLanguages,
  type LocationExperienceLink,
  type LocationExperienceLinkInput,
  type LocationExperienceRoomLite,
  type ExperienceFullRow,
  type ExperienceLocationLink,
  type ExperienceLocationLinkInput,
  type ExperienceLanguageRow,
} from '../../lib/setup';
import { useLocationFilter } from '../../lib/location-context';

interface DraftLink {
  draftId:        string;
  experience_id:  string;
  experience_name: string;
  validFrom:      string;
  validUntil:     string;
  comingSoon:     boolean;  // status='coming_soon': zichtbaar in de widget maar niet boekbaar
  releaseEstimate: string;  // vrije tekst op de widget-kaart, bv. "Expected February 2027"
  infoUrl:        string;   // optionele doorklik-URL van de coming-soon-kaart
  roomIds:        Set<string>; // empty = all rooms (single-room locations always empty)
}

let draftIdCounter = 0;
const newDraftId = () => `draft-${++draftIdCounter}`;

function snapshotToDrafts(experiences: LocationExperienceLink[]): DraftLink[] {
  return experiences.map((le) => ({
    draftId:         le.id,
    experience_id:   le.experience_id,
    experience_name: le.experience_name,
    validFrom:       le.valid_from,
    validUntil:      le.valid_until,
    // Legacy 'inactive' rows surface as gewone (actieve) drafts — opslaan
    // maakte ze voorheen ook al actief (replace-all schrijft altijd actief).
    comingSoon:      le.status === 'coming_soon',
    releaseEstimate: le.release_estimate ?? '',
    infoUrl:         le.info_url ?? '',
    roomIds:         new Set(le.room_ids),
  }));
}

export function ExperiencesTab() {
  const { operator } = useAuth();
  const { locationId: globalLocationId, setLocationId: setGlobalLocationId } =
    useLocationFilter();

  const [locations, setLocations]     = useState<BookingLocation[]>([]);
  // Every experience, also those without a booking_slug (the walk-in refdata
  // only lists slugged ones), so a new experience can be linked here and get
  // its slug via "Bewerken metadata".
  const [allExperiences, setAllExperiences] = useState<ExperienceFullRow[]>([]);
  const [refLoading, setRefLoading]   = useState(true);
  const [refError, setRefError]       = useState<string | null>(null);

  const [locationId, setLocationId]   = useState<string>('');

  const [rooms, setRooms]             = useState<LocationExperienceRoomLite[]>([]);
  const [drafts, setDrafts]           = useState<DraftLink[]>([]);
  // Which location the on-screen drafts/rooms belong to. Saving is a
  // replace-all, so we refuse to save unless this matches the selected
  // location — same guard as the Schedule tab against cross-location overwrite.
  const [loadedLocationId, setLoadedLocationId] = useState<string | null>(null);
  const latestLocationRef = useRef<string>('');
  const [snapLoading, setSnapLoading] = useState(false);
  const [snapError, setSnapError]     = useState<string | null>(null);

  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash]   = useState(false);

  const [editingMetaId, setEditingMetaId] = useState<string | null>(null);

  // ---- Initial load ------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRefLoading(true);
      setRefError(null);
      try {
        const [ref, experiences] = await Promise.all([
          fetchWalkinRefData(true),
          listExperiencesFull(),
        ]);
        if (cancelled) return;
        setLocations(ref.locations);
        setAllExperiences(experiences);
        setLocationId(globalLocationId ?? ref.locations[0]?.id ?? '');
      } catch (e) {
        if (!cancelled) setRefError(formatError(e));
      } finally {
        if (!cancelled) setRefLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    latestLocationRef.current = locationId;
  }, [locationId]);

  useEffect(() => {
    if (globalLocationId && globalLocationId !== locationId) {
      setLocationId(globalLocationId);
    }
  }, [globalLocationId, locationId]);

  // ---- Load when location changes ----------------------------------------
  const reload = useCallback(async () => {
    if (!locationId) {
      setRooms([]);
      setDrafts([]);
      setLoadedLocationId(null);
      return;
    }
    const target = locationId;
    setSnapLoading(true);
    setSnapError(null);
    setLoadedLocationId(null);
    setRooms([]);
    setDrafts([]);
    try {
      const s = await getLocationExperiences(target);
      if (latestLocationRef.current !== target) return;
      setRooms(s.rooms);
      setDrafts(snapshotToDrafts(s.experiences));
      setLoadedLocationId(target);
    } catch (e) {
      if (latestLocationRef.current === target) setSnapError(formatError(e));
    } finally {
      if (latestLocationRef.current === target) setSnapLoading(false);
    }
  }, [locationId]);
  useEffect(() => { void reload(); }, [reload]);

  const isMultiRoom = rooms.length > 1;

  const usedExperienceIds = useMemo(
    () => new Set(drafts.map((d) => d.experience_id)),
    [drafts],
  );
  const addableExperiences = useMemo(
    () => allExperiences.filter((e) => !usedExperienceIds.has(e.id)),
    [allExperiences, usedExperienceIds],
  );
  const missingSlugIds = useMemo(
    () => new Set(allExperiences.filter((e) => !e.booking_slug).map((e) => e.id)),
    [allExperiences],
  );

  // Re-read the catalog after a metadata save (slug may have been set)
  // without reloading the drafts, so an unsaved new link stays on screen.
  const refreshCatalog = useCallback(async () => {
    try {
      setAllExperiences(await listExperiencesFull());
    } catch (e) {
      setSubmitError(formatError(e));
    }
  }, []);

  // ---- Mutators ----------------------------------------------------------
  const addExperience = (experienceId: string) => {
    const exp = allExperiences.find((e) => e.id === experienceId);
    if (!exp) return;
    const year = new Date().getFullYear();
    setDrafts((prev) => [
      ...prev,
      {
        draftId:         newDraftId(),
        experience_id:   exp.id,
        experience_name: exp.name,
        validFrom:       `${year}-01-01`,
        validUntil:      `${year}-12-31`,
        comingSoon:      false,
        releaseEstimate: '',
        infoUrl:         '',
        roomIds:         new Set(),
      },
    ]);
  };
  const removeLink = (draftId: string) => {
    setDrafts((prev) => prev.filter((d) => d.draftId !== draftId));
  };
  const updateRange = (
    draftId: string,
    field:   'validFrom' | 'validUntil',
    value:   string,
  ) => {
    setDrafts((prev) =>
      prev.map((d) => (d.draftId === draftId ? { ...d, [field]: value } : d)),
    );
  };
  const updateSoon = (
    draftId: string,
    patch:   Partial<Pick<DraftLink, 'comingSoon' | 'releaseEstimate' | 'infoUrl'>>,
  ) => {
    setDrafts((prev) =>
      prev.map((d) => (d.draftId === draftId ? { ...d, ...patch } : d)),
    );
  };
  const toggleRoom = (draftId: string, roomId: string) => {
    setDrafts((prev) =>
      prev.map((d) => {
        if (d.draftId !== draftId) return d;
        const rs = new Set(d.roomIds);
        if (rs.has(roomId)) rs.delete(roomId); else rs.add(roomId);
        return { ...d, roomIds: rs };
      }),
    );
  };
  // Reorder: draft array order is persisted as sort_order on save (widget
  // picker order). Moving swaps a draft with its neighbour.
  const moveDraft = (draftId: string, dir: -1 | 1) => {
    setDrafts((prev) => {
      const i = prev.findIndex((d) => d.draftId === draftId);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
  };

  // ---- Save --------------------------------------------------------------
  const handleSave = useCallback(async () => {
    if (!operator?.id || !locationId || submitting) return;
    // Never write drafts loaded for a different location (replace-all save).
    if (loadedLocationId !== locationId) return;

    for (let i = 0; i < drafts.length; i++) {
      const d = drafts[i]!;
      // The widget addresses experiences by slug; never publish one without.
      if (missingSlugIds.has(d.experience_id)) {
        setSubmitError(`${d.experience_name}: stel eerst een slug in via "Bewerken metadata".`);
        return;
      }
      if (!d.validFrom || !d.validUntil) {
        setSubmitError(`${d.experience_name}: vul beide datums in.`);
        return;
      }
      if (d.validUntil < d.validFrom) {
        setSubmitError(`${d.experience_name}: einddatum mag niet vóór startdatum liggen.`);
        return;
      }
      const infoUrl = d.infoUrl.trim();
      if (d.comingSoon && infoUrl !== '' && !/^https?:\/\//i.test(infoUrl)) {
        setSubmitError(`${d.experience_name}: info-link moet met http(s):// beginnen.`);
        return;
      }
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: LocationExperienceLinkInput[] = drafts.map((d) => ({
        experience_id:    d.experience_id,
        valid_from:       d.validFrom,
        valid_until:      d.validUntil,
        status:           d.comingSoon ? 'coming_soon' : 'active',
        release_estimate: d.comingSoon ? d.releaseEstimate.trim() || null : null,
        info_url:         d.comingSoon ? d.infoUrl.trim() || null : null,
        // Only send room_ids for multi-room locations; single-room locations
        // always send [] (= "all rooms"), which is the only sensible value.
        room_ids:      isMultiRoom ? Array.from(d.roomIds) : [],
      }));
      await saveLocationExperiences(operator.id, locationId, payload);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
      void reload();
    } catch (e) {
      setSubmitError(formatError(e));
    } finally {
      setSubmitting(false);
    }
  }, [operator?.id, locationId, submitting, drafts, isMultiRoom, reload, loadedLocationId, missingSlugIds]);

  // ---- Render -----------------------------------------------------------
  if (refLoading) return <div className="muted">Laden…</div>;
  if (refError) return <div className="error-card">{refError}</div>;

  return (
    <div className="setup-experiences">
      <div className="walkin-row">
        <label className="walkin-field">
          <span>Locatie</span>
          <select
            value={locationId}
            onChange={(e) => {
              const id = e.target.value;
              setLocationId(id);
              setGlobalLocationId(id);
            }}
          >
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.display_name ?? l.name}</option>
            ))}
          </select>
        </label>
      </div>

      <p className="muted setup-schedule-hint">
        Iedere experience heeft een beschikbaarheidsperiode. De experience verschijnt
        alleen in boekingen tijdens actieve openingsregels binnen deze datums. De
        volgorde hieronder (↑ ↓) bepaalt de volgorde in de boekingswidget — opslaan
        om te bewaren.
      </p>

      {snapLoading ? <div className="muted">Laden…</div> : null}
      {snapError ? <div className="error-card">{snapError}</div> : null}

      {!snapLoading && !snapError ? (
        <>
          {drafts.length === 0 ? (
            <p className="muted setup-rules-empty">
              Nog geen experiences gekoppeld aan deze locatie.
            </p>
          ) : null}

          {drafts.map((d, i) => (
            <ExperienceLinkCard
              key={d.draftId}
              link={d}
              rooms={rooms}
              isMultiRoom={isMultiRoom}
              missingSlug={missingSlugIds.has(d.experience_id)}
              isEditingMeta={editingMetaId === d.experience_id}
              canMoveUp={i > 0}
              canMoveDown={i < drafts.length - 1}
              onMoveUp={() => moveDraft(d.draftId, -1)}
              onMoveDown={() => moveDraft(d.draftId, 1)}
              onUpdateRange={(field, value) => updateRange(d.draftId, field, value)}
              onUpdateSoon={(patch) => updateSoon(d.draftId, patch)}
              onToggleRoom={(rid) => toggleRoom(d.draftId, rid)}
              onRemove={() => removeLink(d.draftId)}
              onOpenMeta={() =>
                setEditingMetaId(editingMetaId === d.experience_id ? null : d.experience_id)
              }
              onCloseMeta={() => setEditingMetaId(null)}
              onMetaSaved={() => {
                setEditingMetaId(null);
                void refreshCatalog();
              }}
              operatorId={operator?.id ?? ''}
            />
          ))}

          {addableExperiences.length > 0 ? (
            <AddExperiencePicker
              addableExperiences={addableExperiences}
              onAdd={addExperience}
            />
          ) : null}

          {submitError ? (
            <div className="error-card walkin-submit-error">{submitError}</div>
          ) : null}

          <div className="walkin-step-actions">
            <span className="muted">{savedFlash ? '✓ Opgeslagen' : null}</span>
            <button
              className="walkin-primary-btn"
              onClick={() => void handleSave()}
              disabled={
                submitting || !locationId || loadedLocationId !== locationId
              }
            >
              {submitting ? 'Bezig…' : 'Opslaan'}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

// ============================================================================
// ExperienceLinkCard
// ============================================================================
function ExperienceLinkCard({
  link, rooms, isMultiRoom, missingSlug, isEditingMeta,
  canMoveUp, canMoveDown, onMoveUp, onMoveDown,
  onUpdateRange, onUpdateSoon, onToggleRoom, onRemove,
  onOpenMeta, onCloseMeta, onMetaSaved,
  operatorId,
}: {
  link:           DraftLink;
  rooms:          LocationExperienceRoomLite[];
  isMultiRoom:    boolean;
  missingSlug:    boolean;
  isEditingMeta:  boolean;
  canMoveUp:      boolean;
  canMoveDown:    boolean;
  onMoveUp:       () => void;
  onMoveDown:     () => void;
  onUpdateRange:  (field: 'validFrom' | 'validUntil', value: string) => void;
  onUpdateSoon:   (patch: Partial<Pick<DraftLink, 'comingSoon' | 'releaseEstimate' | 'infoUrl'>>) => void;
  onToggleRoom:   (roomId: string) => void;
  onRemove:       () => void;
  onOpenMeta:     () => void;
  onCloseMeta:    () => void;
  onMetaSaved:    () => void;
  operatorId:     string;
}) {
  return (
    <div className="setup-rule-card">
      <div className="setup-rule-card-head">
        <div className="setup-rule-reorder">
          <button
            className="setup-rule-move"
            onClick={onMoveUp}
            disabled={!canMoveUp}
            aria-label="Naar boven verplaatsen"
            title="Naar boven"
          >
            ↑
          </button>
          <button
            className="setup-rule-move"
            onClick={onMoveDown}
            disabled={!canMoveDown}
            aria-label="Naar beneden verplaatsen"
            title="Naar beneden"
          >
            ↓
          </button>
        </div>
        <strong>{link.experience_name}</strong>
        <button
          className="setup-rule-remove"
          onClick={onRemove}
          aria-label="Experience verwijderen van locatie"
          title="Verwijderen van locatie"
        >
          ✕
        </button>
      </div>

      <div className="walkin-row setup-rule-range">
        <label className="walkin-field">
          <span>Beschikbaar vanaf</span>
          <input
            type="date"
            value={link.validFrom}
            onChange={(e) => onUpdateRange('validFrom', e.target.value)}
          />
        </label>
        <label className="walkin-field">
          <span>Beschikbaar tot</span>
          <input
            type="date"
            value={link.validUntil}
            onChange={(e) => onUpdateRange('validUntil', e.target.value)}
          />
        </label>
      </div>

      {/* Coming soon: zichtbaar in de widget-picker (badge, niet boekbaar).
          Verwachte release + info-link kleden de kaart optioneel aan. */}
      <label className="setup-weekday-pill">
        <input
          type="checkbox"
          checked={link.comingSoon}
          onChange={(e) => onUpdateSoon({ comingSoon: e.target.checked })}
        />
        <span>Binnenkort beschikbaar (zichtbaar, niet boekbaar)</span>
      </label>

      {link.comingSoon ? (
        <div className="walkin-row">
          <label className="walkin-field">
            <span>Verwachte release (optioneel, EN)</span>
            <input
              type="text"
              value={link.releaseEstimate}
              onChange={(e) => onUpdateSoon({ releaseEstimate: e.target.value })}
              placeholder="Expected February 2027"
            />
          </label>
          <label className="walkin-field">
            <span>Info-link (optioneel)</span>
            <input
              type="url"
              value={link.infoUrl}
              onChange={(e) => onUpdateSoon({ infoUrl: e.target.value })}
              placeholder="https://…"
            />
          </label>
        </div>
      ) : null}

      {isMultiRoom ? (
        <div className="setup-rule-row">
          <div className="setup-rule-label">Ruimtes</div>
          <div className="setup-weekday-row">
            {rooms.map((r) => (
              <label key={r.id} className="setup-weekday-pill">
                <input
                  type="checkbox"
                  checked={link.roomIds.has(r.id)}
                  onChange={() => onToggleRoom(r.id)}
                />
                <span>{r.name}</span>
              </label>
            ))}
          </div>
          <p className="muted setup-rule-room-hint">
            Geen aangevinkt = alle ruimtes op deze locatie.
          </p>
        </div>
      ) : null}

      {missingSlug ? (
        <div className="error-card">
          Deze experience heeft nog geen slug. Stel er een in via
          "Bewerken metadata" voordat je opslaat.
        </div>
      ) : null}

      <div className="setup-rule-meta-row">
        <button className="btn-link" onClick={onOpenMeta}>
          {isEditingMeta ? '↑ Sluit metadata' : 'Bewerken metadata →'}
        </button>
      </div>

      {isEditingMeta ? (
        <ExperienceMetaEditor
          experienceId={link.experience_id}
          operatorId={operatorId}
          onCancel={onCloseMeta}
          onSaved={onMetaSaved}
        />
      ) : null}
    </div>
  );
}

// ============================================================================
// AddExperiencePicker
// ============================================================================
function AddExperiencePicker({
  addableExperiences, onAdd,
}: {
  addableExperiences: ExperienceFullRow[];
  onAdd: (id: string) => void;
}) {
  const [pendingId, setPendingId] = useState('');
  return (
    <div className="setup-add-experience">
      <select
        value={pendingId}
        onChange={(e) => setPendingId(e.target.value)}
      >
        <option value="">— Kies een experience —</option>
        {addableExperiences.map((e) => (
          <option key={e.id} value={e.id}>
            {e.booking_slug ? e.name : `${e.name} (geen slug)`}
          </option>
        ))}
      </select>
      <button
        className="btn-link"
        disabled={!pendingId}
        onClick={() => {
          if (pendingId) {
            onAdd(pendingId);
            setPendingId('');
          }
        }}
      >
        + Experience toevoegen
      </button>
    </div>
  );
}

// ============================================================================
// ExperienceMetaEditor
// ============================================================================
// Reused inline editor for the per-experience catalog fields (was the body of
// the legacy /setup?tab=experiences screen). Adds a "Ook beschikbaar op"
// pane that mirrors location_experience writes from the experience side.
function ExperienceMetaEditor({
  experienceId, operatorId, onCancel, onSaved,
}: {
  experienceId: string;
  operatorId:   string;
  onCancel:     () => void;
  onSaved:      () => void;
}) {
  const [row, setRow] = useState<ExperienceFullRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const [priceEur, setPriceEur]       = useState('');
  const [duration, setDuration]       = useState('');
  const [imageUrl, setImageUrl]       = useState('');
  const [slug, setSlug]               = useState('');
  const [submitting, setSubmitting]   = useState(false);

  // Per-language name + description matrix (experience_language_support).
  const [langRows, setLangRows]   = useState<ExperienceLanguageRow[]>([]);
  const [langBusy, setLangBusy]   = useState(false);
  const [langErr, setLangErr]     = useState<string | null>(null);
  const [langSaved, setLangSaved] = useState(false);

  const [mirrorLinks, setMirrorLinks] = useState<ExperienceLocationLink[]>([]);
  const [mirrorEdit, setMirrorEdit]   = useState<ExperienceLocationLinkInput[]>([]);
  const [mirrorBusy, setMirrorBusy]   = useState(false);
  const [mirrorErr, setMirrorErr]     = useState<string | null>(null);
  const [mirrorSaved, setMirrorSaved] = useState(false);

  // Initial load.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [list, locs, langs] = await Promise.all([
          listExperiencesFull(),
          getExperienceLocations(experienceId),
          getExperienceLanguages(experienceId),
        ]);
        if (cancelled) return;
        const r = list.find((x) => x.id === experienceId) ?? null;
        setRow(r);
        if (r) {
          setPriceEur((r.booking_base_price_cents / 100).toFixed(2));
          setDuration(String(r.duration_minutes));
          setImageUrl(r.booking_image_url ?? '');
          setSlug(r.booking_slug ?? '');
        }
        setLangRows(langs);
        setMirrorLinks(locs.links);
        // Round-trip de coming-soon-velden: de save is replace-all, dus zonder
        // pass-through zou een mirror-save ze terugzetten naar 'active'.
        setMirrorEdit(locs.links.map((l) => ({
          location_id:      l.location_id,
          valid_from:       l.valid_from,
          valid_until:      l.valid_until,
          status:           l.status === 'coming_soon' ? 'coming_soon' : 'active',
          release_estimate: l.release_estimate,
          info_url:         l.info_url,
          room_ids:         l.room_ids,
        })));
      } catch (e) {
        if (!cancelled) setError(formatError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [experienceId]);

  const handleSaveMeta = useCallback(async () => {
    if (submitting || !operatorId || !row) return;
    setSubmitting(true);
    setError(null);
    try {
      const priceCents = Math.round(Number(priceEur) * 100);
      const dur = Number(duration);
      if (!Number.isFinite(priceCents) || priceCents < 0) throw new Error('Prijs ongeldig');
      if (!Number.isFinite(dur) || dur <= 0) throw new Error('Duur moet > 0 zijn');
      // display_name is now edited per-language in the language matrix below,
      // so this path leaves it untouched (p_display_name = null = no-op).
      await editExperience(operatorId, {
        experienceId:             row.id,
        display_name:             null,
        booking_base_price_cents: priceCents,
        duration_minutes:         dur,
        booking_image_url:        imageUrl.trim(),
        booking_slug:             slug.trim().length > 0 ? slug.trim() : null,
      });
      onSaved();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setSubmitting(false);
    }
  }, [submitting, operatorId, row, priceEur, duration, imageUrl, slug, onSaved]);

  // ---- Language matrix (name + description per language) -----------------
  const updateLangField = (
    languageId: string,
    field:      'display_name' | 'description',
    value:      string,
  ) => {
    setLangRows((prev) =>
      prev.map((r) => (r.language_id === languageId ? { ...r, [field]: value } : r)),
    );
  };

  const handleSaveLanguages = useCallback(async () => {
    if (langBusy || !operatorId) return;
    setLangBusy(true);
    setLangErr(null);
    try {
      await saveExperienceLanguages(
        operatorId,
        experienceId,
        langRows.map((r) => ({
          language_id:  r.language_id,
          display_name: r.display_name && r.display_name.trim() ? r.display_name.trim() : null,
          description:  r.description && r.description.trim() ? r.description.trim() : null,
        })),
      );
      setLangSaved(true);
      setTimeout(() => setLangSaved(false), 2000);
      const fresh = await getExperienceLanguages(experienceId);
      setLangRows(fresh);
    } catch (e) {
      setLangErr(formatError(e));
    } finally {
      setLangBusy(false);
    }
  }, [langBusy, operatorId, experienceId, langRows]);

  // Mirror toggles: tick/untick a location.
  const toggleMirrorLocation = (locationId: string, locationLabel: string) => {
    setMirrorEdit((prev) => {
      const existing = prev.find((l) => l.location_id === locationId);
      if (existing) {
        return prev.filter((l) => l.location_id !== locationId);
      }
      // New row gets default range. We cannot know the location's full-room
      // catalog here without another query — empty room_ids ⇒ all rooms. The
      // location-side editor remains the place to scope down to specific rooms.
      const year = new Date().getFullYear();
      void locationLabel;
      return [
        ...prev,
        {
          location_id:      locationId,
          valid_from:       `${year}-01-01`,
          valid_until:      `${year}-12-31`,
          status:           'active',
          release_estimate: null,
          info_url:         null,
          room_ids:         [],
        },
      ];
    });
  };
  const updateMirrorRange = (
    locationId: string,
    field:      'valid_from' | 'valid_until',
    value:      string,
  ) => {
    setMirrorEdit((prev) =>
      prev.map((l) => (l.location_id === locationId ? { ...l, [field]: value } : l)),
    );
  };

  const handleSaveMirror = useCallback(async () => {
    if (mirrorBusy || !operatorId) return;
    setMirrorBusy(true);
    setMirrorErr(null);
    try {
      await setExperienceLocations(operatorId, experienceId, mirrorEdit);
      setMirrorSaved(true);
      setTimeout(() => setMirrorSaved(false), 2000);
      // Refresh the canonical link list so future toggles see post-save room_ids.
      const fresh = await getExperienceLocations(experienceId);
      setMirrorLinks(fresh.links);
      setMirrorEdit(fresh.links.map((l) => ({
        location_id:      l.location_id,
        valid_from:       l.valid_from,
        valid_until:      l.valid_until,
        status:           l.status === 'coming_soon' ? 'coming_soon' : 'active',
        release_estimate: l.release_estimate,
        info_url:         l.info_url,
        room_ids:         l.room_ids,
      })));
    } catch (e) {
      setMirrorErr(formatError(e));
    } finally {
      setMirrorBusy(false);
    }
  }, [mirrorBusy, operatorId, experienceId, mirrorEdit]);

  // Show all locations the experience could be linked to. We use mirrorLinks
  // (currently linked) plus mirrorEdit (any pending additions). This editor
  // doesn't fetch the full location catalog — adding a new location to an
  // experience from this side is a niche flow; the location-side editor is
  // the primary place to add brand-new (location, experience) links.
  const allKnownLocations = useMemo(() => {
    const map = new Map<string, { id: string; label: string }>();
    mirrorLinks.forEach((l) => {
      map.set(l.location_id, { id: l.location_id, label: l.location_name });
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [mirrorLinks]);

  if (loading) return <div className="muted setup-meta-editor">Laden…</div>;
  if (error && !row) return <div className="error-card setup-meta-editor">{error}</div>;
  if (!row) return null;

  return (
    <div className="setup-meta-editor walkin-step">
      <label className="walkin-field">
        <span>Slug</span>
        <input
          type="text"
          value={slug}
          onChange={(e) => setSlug(e.target.value.toLowerCase())}
          placeholder="pharaohs"
        />
      </label>
      <div className="walkin-row">
        <label className="walkin-field">
          <span>Prijs (€)</span>
          <input
            type="number"
            min={0}
            step="0.01"
            value={priceEur}
            onChange={(e) => setPriceEur(e.target.value)}
          />
        </label>
        <label className="walkin-field">
          <span>Duur (min)</span>
          <input
            type="number"
            min={1}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
          />
        </label>
      </div>
      <label className="walkin-field">
        <span>Afbeelding-URL</span>
        <input
          type="url"
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
          placeholder="https://…"
        />
      </label>

      {error ? (
        <div className="error-card walkin-submit-error">{error}</div>
      ) : null}

      <div className="walkin-step-actions">
        <button className="btn-link" onClick={onCancel} disabled={submitting}>
          Annuleren
        </button>
        <button
          className="walkin-primary-btn"
          onClick={() => void handleSaveMeta()}
          disabled={submitting}
        >
          {submitting ? 'Bezig…' : 'Metadata opslaan'}
        </button>
      </div>

      <div className="setup-meta-languages">
        <div className="setup-section-head">
          Namen &amp; beschrijvingen
          <span className="muted">
            {' '}— per taal, zoals getoond in de boekingswidget. Leeg = geen kaart
            voor die taal (er wordt teruggevallen op een andere taal).
          </span>
        </div>
        {langRows.length === 0 ? (
          <p className="muted">Geen talen geconfigureerd.</p>
        ) : (
          <div className="setup-lang-grid">
            {langRows.map((r) => (
              <div key={r.language_id} className="setup-lang-block">
                <div className="setup-lang-head">{r.language_name} ({r.short_name})</div>
                <label className="walkin-field">
                  <span>Naam</span>
                  <input
                    type="text"
                    value={r.display_name ?? ''}
                    onChange={(e) => updateLangField(r.language_id, 'display_name', e.target.value)}
                    placeholder={row.name}
                  />
                </label>
                <label className="walkin-field">
                  <span>Beschrijving</span>
                  <textarea
                    rows={3}
                    value={r.description ?? ''}
                    onChange={(e) => updateLangField(r.language_id, 'description', e.target.value)}
                    placeholder="Korte omschrijving voor de widget…"
                  />
                </label>
              </div>
            ))}
          </div>
        )}
        {langErr ? (
          <div className="error-card walkin-submit-error">{langErr}</div>
        ) : null}
        <div className="walkin-step-actions">
          <span className="muted">{langSaved ? '✓ Opgeslagen' : null}</span>
          <button
            className="walkin-primary-btn"
            onClick={() => void handleSaveLanguages()}
            disabled={langBusy || !operatorId || langRows.length === 0}
          >
            {langBusy ? 'Bezig…' : 'Namen opslaan'}
          </button>
        </div>
      </div>

      <div className="setup-meta-mirror">
        <div className="setup-section-head">
          Ook beschikbaar op
          <span className="muted">
            {' '}— spiegel van de locatie-kant. Vinkt een locatie aan/uit en past datums aan.
          </span>
        </div>
        {allKnownLocations.length === 0 ? (
          <p className="muted">
            Deze experience is nog niet aan een locatie gekoppeld. Open de Schedule-tab
            van de gewenste locatie en voeg de experience daar toe.
          </p>
        ) : (
          <ul className="setup-meta-mirror-list">
            {allKnownLocations.map((loc) => {
              const linked = mirrorEdit.find((l) => l.location_id === loc.id);
              return (
                <li key={loc.id} className="setup-meta-mirror-row">
                  <label className="setup-meta-mirror-toggle">
                    <input
                      type="checkbox"
                      checked={linked !== undefined}
                      onChange={() => toggleMirrorLocation(loc.id, loc.label)}
                    />
                    <span>{loc.label}</span>
                  </label>
                  {linked ? (
                    <div className="setup-meta-mirror-range">
                      <input
                        type="date"
                        value={linked.valid_from}
                        onChange={(e) => updateMirrorRange(loc.id, 'valid_from', e.target.value)}
                      />
                      <span className="setup-period-arrow">→</span>
                      <input
                        type="date"
                        value={linked.valid_until}
                        onChange={(e) => updateMirrorRange(loc.id, 'valid_until', e.target.value)}
                      />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
        {mirrorErr ? (
          <div className="error-card walkin-submit-error">{mirrorErr}</div>
        ) : null}
        <div className="walkin-step-actions">
          <span className="muted">{mirrorSaved ? '✓ Opgeslagen' : null}</span>
          <button
            className="walkin-primary-btn"
            onClick={() => void handleSaveMirror()}
            disabled={mirrorBusy || !operatorId}
          >
            {mirrorBusy ? 'Bezig…' : 'Locaties opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}
